import * as React from 'react';
import { Modal } from 'antd';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import InternalTeamSelect from '../internal_team_select/InternalTeamSelect';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

require('./ProjectInternalTeamsField.less');

// Which internal teams a project is scoped to (API field: supplierFacilityIds).
// Shared by ProjectCreateForm and ProjectEditForm, which are otherwise near
// duplicates where a pasted field would drift.
//
// "Select All" is the empty list — the API reads no ids as "every internal tech of
// the buyer company", which is how projects behaved before this field existed. Only
// a non-empty list narrows access, and the backend enforces that on reads, writes
// and list filters, cascading to the project's deliverables, tasks and notes.
//
// A side benefit of the toggle: with All on, the picker isn't mounted at all, so a
// buyer with no internal teams never meets a disabled, empty dropdown.
export const INTERNAL_TEAMS_ALL_FIELD = 'allInternalTeams';
export const INTERNAL_TEAMS_FIELD = 'supplierFacilityIds';

// Form initialValues for both fields, from a project record (or {} when creating).
export const getInternalTeamsInitialValues = (formData) => ({
	[INTERNAL_TEAMS_FIELD]: nullSafeGetOrElse(INTERNAL_TEAMS_FIELD, formData, []),
	[INTERNAL_TEAMS_ALL_FIELD]: nullSafeGetOrElse(INTERNAL_TEAMS_FIELD, formData, []).length === 0,
});

// The ids to send, given submitted form values. `null` means "don't narrow": create
// omits the field entirely, while update has to send an explicit empty array,
// because undefined is dropped by JSON.stringify and the backend would keep the
// project's existing list instead of clearing it.
export const getInternalTeamIdsFromValues = (values) =>
	values[INTERNAL_TEAMS_ALL_FIELD] ||
	nullSafeGetOrElse(INTERNAL_TEAMS_FIELD, values, []).length === 0
		? null
		: values[INTERNAL_TEAMS_FIELD];

// An internal tech may save a list that leaves their own team out — the API allows the
// write but reads honor the list, so the project becomes unreadable to them the moment
// it is saved. Buyers always see every project, and an empty list (`null` here) is
// visible to every team, so neither can lock themselves out.
export interface CurrentUserForAccess {
	userType?: string;
	facility?: { id?: number };
}

export const willLoseProjectAccess = (
	currentUser: CurrentUserForAccess,
	internalTeamIds: number[] | null
): boolean =>
	nullSafeGet('userType', currentUser) === ROLE_TYPES.SUPPLIER &&
	internalTeamIds !== null &&
	!internalTeamIds.includes(nullSafeGet('facility.id', currentUser));

// Resolves true when the user confirms, false when they back out.
export const confirmLosingProjectAccess = (): Promise<boolean> =>
	new Promise((resolve) => {
		Modal.confirm({
			title: 'Remove your own team from this project?',
			content:
				'Your team will no longer be able to see this project, its deliverables, tasks or notes after you save.',
			okText: 'Save',
			cancelText: 'Cancel',
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});

const ProjectInternalTeamsField = ({ form }) => (
	<div className="project-internal-teams-field">
		<SelectWithAllToggle
			label="Internal Teams"
			name={INTERNAL_TEAMS_FIELD}
			allName={INTERNAL_TEAMS_ALL_FIELD}
			form={form}
			displaySelectAllWithLabel
			// Select All off with nothing picked would send "no ids", which the API reads as
			// every internal team — the opposite of what the field says. Block it instead.
			requiredIfNotAllSelected
			selectAllPlaceholder="All internal teams"
			defaultFormFieldStyle={{ maxWidth: 640 }}
		>
			<InternalTeamSelect />
		</SelectWithAllToggle>
		<div className="footnoteregular mb-4 text-character-secondary" style={{ maxWidth: 640 }}>
			Only the teams picked here will see this project, along with its deliverables, tasks and
			notes. Select All keeps it visible to every internal team.
		</div>
	</div>
);

export default ProjectInternalTeamsField;
